import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, Cell
} from 'recharts';
import { Calculator, Zap, FileText, Mail, FileCheck, Image, ArrowUpDown, ChevronUp, ChevronDown, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

// ─── Model Data (prices per 1k tokens) ────────────────────────────────────────
// Fetched from API at runtime; empty array while loading
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Provider colour palette ──────────────────────────────────────────────────
const PROVIDER_COLORS = {
  OpenAI:    '#10b981',
  Anthropic: '#f59e0b',
  Google:    '#3b82f6',
  Meta:      '#8b5cf6',
  xAI:       '#ef4444',
  DeepSeek:  '#06b6d4',
  Cohere:    '#f97316',
  Kimi:      '#ec4899',
};

// ─── Quick presets ─────────────────────────────────────────────────────────────
const PRESETS = [
  {
    label: 'Blog Writing',
    icon: FileText,
    inputTokens: 200, outputTokens: 1350, apiCalls: 30,
    description: '~1,000-word blog post, 30 posts/month',
  },
  {
    label: 'Cold Email',
    icon: Mail,
    inputTokens: 100, outputTokens: 270, apiCalls: 100,
    description: '~200-word email, 100 sends/month',
  },
  {
    label: 'Proposal Writing',
    icon: FileCheck,
    inputTokens: 1500, outputTokens: 4050, apiCalls: 10,
    description: '~3,000-word proposal, 10/month',
  },
  {
    label: 'Image Creation',
    icon: Image,
    inputTokens: 150, outputTokens: 200, apiCalls: 200,
    description: 'LLM-refined image prompts, 200/month',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseContext = (ctx) => {
  const n = parseFloat(ctx);
  if (ctx.endsWith('M') || ctx.endsWith('m')) return n * 1000;
  return n;
};

const fmtUsd = (v, decimals = 4) =>
  v === 0 ? '$0.00' : `$${v.toFixed(decimals)}`;

// Custom Recharts tooltip so it looks good in dark mode
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold mb-1 text-foreground">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color ?? p.fill }}>
          {p.name}: {fmtUsd(p.value, 4)}
        </p>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LLMCostCalculator() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [models, setModels]         = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);

  const [inputVal,  setInputVal]  = useState(500);
  const [outputVal, setOutputVal] = useState(1000);
  const [apiCalls,  setApiCalls]  = useState(100);
  const [unit,        setUnit]        = useState('tokens');
  const [activePreset, setActivePreset] = useState(null);
  const [sortField, setSortField] = useState('total');
  const [sortDir,   setSortDir]   = useState('asc');
  const [filterProvider, setFilterProvider] = useState('');
  const [promptContext, setPromptContext] = useState('');

  // Sync URL params → state on every navigation (handles re-visits)
  useEffect(() => {
    const qInput  = searchParams.get('input');
    const qOutput = searchParams.get('output');
    const qCalls  = searchParams.get('calls');
    const qTitle  = searchParams.get('title');

    if (qInput || qOutput || qTitle) {
      if (qInput)  setInputVal(Number(qInput));
      if (qOutput) setOutputVal(Number(qOutput));
      if (qCalls)  setApiCalls(Number(qCalls));
      setPromptContext(qTitle || '');
      setActivePreset(null);
      setFilterProvider('');
      // Clean URL after reading
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    axios.get(`${API_BASE}/llm-models`)
      .then(res => setModels(res.data.models.map(m => ({
        ...m,
        input:  m.input_per_1k,
        output: m.output_per_1k,
      }))))
      .catch(() => {})
      .finally(() => setModelsLoading(false));
  }, []);

  const toTokens = (val) => {
    if (unit === 'words')      return Math.ceil(val * 0.75);
    if (unit === 'characters') return Math.ceil(val / 4);
    return val;
  };

  const inputTokens  = toTokens(inputVal);
  const outputTokens = toTokens(outputVal);

  const results = useMemo(() => models.map(m => {
    const inputCost  = m.input  * inputTokens  / 1000 * apiCalls;
    const outputCost = m.output * outputTokens / 1000 * apiCalls;
    const total   = inputCost + outputCost;
    const perCall = total / Math.max(apiCalls, 1);
    return { ...m, inputCost, outputCost, perCall, total };
  }), [models, inputTokens, outputTokens, apiCalls]);

  const filtered = useMemo(() => {
    let r = filterProvider
      ? results.filter(m => m.provider === filterProvider)
      : [...results];
    r.sort((a, b) => {
      const mult = sortDir === 'asc' ? 1 : -1;
      if (sortField === 'provider') return mult * a.provider.localeCompare(b.provider);
      if (sortField === 'model')    return mult * a.model.localeCompare(b.model);
      if (sortField === 'context')  return mult * (parseContext(a.context) - parseContext(b.context));
      return mult * (a[sortField] - b[sortField]);
    });
    return r;
  }, [results, filterProvider, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const handlePreset = (preset, idx) => {
    setInputVal(preset.inputTokens);
    setOutputVal(preset.outputTokens);
    setApiCalls(preset.apiCalls);
    setUnit('tokens');
    setActivePreset(idx);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-25" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-primary" />
      : <ChevronDown className="w-3 h-3 text-primary" />;
  };

  // ── Chart data ──────────────────────────────────────────────────────────────
  const cheapest12 = useMemo(() =>
    [...results].sort((a, b) => a.total - b.total).slice(0, 12).map(m => ({
      name:     m.model.length > 17 ? m.model.slice(0, 17) + '…' : m.model,
      total:    parseFloat(m.total.toFixed(5)),
      provider: m.provider,
    })),
  [results]);

  const providerAvg = useMemo(() => {
    const grouped = {};
    results.forEach(m => {
      if (!grouped[m.provider]) grouped[m.provider] = [];
      grouped[m.provider].push(m.total);
    });
    return Object.entries(grouped)
      .map(([provider, totals]) => ({
        provider,
        avg: parseFloat((totals.reduce((a, b) => a + b, 0) / totals.length).toFixed(4)),
      }))
      .sort((a, b) => a.avg - b.avg);
  }, [results]);

  const inputOutputSplit = useMemo(() =>
    [...results].sort((a, b) => a.total - b.total).slice(0, 10).map(m => ({
      name:   m.model.length > 14 ? m.model.slice(0, 14) + '…' : m.model,
      Input:  parseFloat(m.inputCost.toFixed(4)),
      Output: parseFloat(m.outputCost.toFixed(4)),
    })),
  [results]);

  const providers = [...new Set(models.map(m => m.provider))].sort();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="llm-calculator-page">

      {/* ── Header ── */}
      <div className="mb-8">
        <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 rounded-full px-3 py-1 mb-4 inline-flex">
          <Zap className="w-3 h-3 mr-1.5" /> Free Tool
        </Badge>
        <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 rounded-full px-2.5 py-0.5 mb-4 ml-2 inline-flex text-[10px] font-bold uppercase tracking-wider">
          Beta
        </Badge>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3">
          LLM Cost <span className="text-primary">Calculator</span>
        </h1>
        <p className="text-base text-muted-foreground max-w-2xl leading-relaxed">
          Estimate and compare API costs across OpenAI, Anthropic, Google, Meta, xAI, DeepSeek and more.
          Enter your usage below or pick a quick preset.
        </p>
      </div>

      {/* ── Prompt context banner ── */}
      {promptContext && (
        <div className="mb-6 flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-5 py-3" data-testid="prompt-context-banner">
          <DollarSign className="w-4 h-4 text-primary shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Estimating cost for prompt:</p>
            <p className="text-sm font-semibold truncate">{promptContext}</p>
          </div>
          <button
            onClick={() => { setPromptContext(''); setInputVal(500); setOutputVal(1000); setApiCalls(100); setFilterProvider(''); }}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground shrink-0"
            data-testid="clear-prompt-context"
          >
            Clear
          </button>
        </div>
      )}

      {/* ── Quick presets ── */}
      <div className="mb-6">
        <p className="text-sm font-medium text-muted-foreground mb-3">Quick compare for:</p>
        <div className="flex flex-wrap gap-2" data-testid="preset-buttons">
          {PRESETS.map((p, i) => (
            <Button
              key={p.label}
              variant={activePreset === i ? 'default' : 'outline'}
              size="sm"
              className={`rounded-full gap-2 h-9 transition-all ${activePreset === i ? 'shadow-lg shadow-primary/20' : 'border-border/60'}`}
              onClick={() => handlePreset(p, i)}
              data-testid={`preset-${p.label.toLowerCase().replace(' ', '-')}`}
            >
              <p.icon className="w-3.5 h-3.5" /> {p.label}
            </Button>
          ))}
        </div>
        {activePreset !== null && (
          <p className="text-xs text-muted-foreground mt-2.5 pl-0.5">
            <span className="text-primary font-semibold">{PRESETS[activePreset].label}:</span>{' '}
            {PRESETS[activePreset].description} —{' '}
            {inputVal.toLocaleString()} input tokens · {outputVal.toLocaleString()} output tokens · {apiCalls} calls/month
          </p>
        )}
      </div>

      {/* ── Calculator inputs ── */}
      <div className="bg-card border border-border/50 rounded-xl p-6 mb-8" data-testid="calculator-inputs">
        <div className="flex items-center gap-2 mb-5">
          <Calculator className="w-4 h-4 text-primary" />
          <span className="font-semibold text-sm">Configure Usage</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground capitalize">Input {unit}</Label>
            <Input
              type="number" min={0} value={inputVal}
              onChange={e => { setInputVal(Number(e.target.value)); setActivePreset(null); }}
              className="h-10"
              data-testid="input-tokens"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground capitalize">Output {unit}</Label>
            <Input
              type="number" min={0} value={outputVal}
              onChange={e => { setOutputVal(Number(e.target.value)); setActivePreset(null); }}
              className="h-10"
              data-testid="output-tokens"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">API Calls / month</Label>
            <Input
              type="number" min={1} value={apiCalls}
              onChange={e => { setApiCalls(Number(e.target.value)); setActivePreset(null); }}
              className="h-10"
              data-testid="api-calls"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Calculate by</Label>
            <div className="flex rounded-lg border border-border overflow-hidden h-10 text-xs">
              {['tokens', 'words', 'chars'].map(u => {
                const fullUnit = u === 'chars' ? 'characters' : u;
                const active   = unit === fullUnit;
                return (
                  <button
                    key={u}
                    onClick={() => setUnit(fullUnit)}
                    className={`flex-1 font-medium capitalize transition-colors ${active ? 'bg-primary text-primary-foreground' : 'hover:bg-accent text-muted-foreground'}`}
                    data-testid={`unit-${u}`}
                  >
                    {u}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Charts row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">

        {/* Chart 1: Cheapest 12 models */}
        <div className="bg-card border border-border/50 rounded-xl p-5" data-testid="chart-cheapest">
          <h3 className="text-sm font-semibold">Top 12 Cheapest Models</h3>
          <p className="text-xs text-muted-foreground mt-0.5 mb-4">Monthly total (USD) — sorted by cost</p>
          <ResponsiveContainer width="100%" height={290}>
            <BarChart data={cheapest12} layout="vertical" margin={{ left: 0, right: 24, top: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(128,128,128,0.12)" />
              <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={v => `$${v}`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={115} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="total" name="Total" radius={[0, 4, 4, 0]}>
                {cheapest12.map((entry, i) => (
                  <Cell key={i} fill={PROVIDER_COLORS[entry.provider] || '#10b981'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 2: Provider avg */}
        <div className="bg-card border border-border/50 rounded-xl p-5" data-testid="chart-provider-avg">
          <h3 className="text-sm font-semibold">Average Cost by Provider</h3>
          <p className="text-xs text-muted-foreground mt-0.5 mb-4">Avg across all models (USD/month)</p>
          <ResponsiveContainer width="100%" height={290}>
            <BarChart data={providerAvg} margin={{ left: 0, right: 10, top: 0, bottom: 44 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(128,128,128,0.12)" />
              <XAxis dataKey="provider" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `$${v}`} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="avg" name="Avg Cost" radius={[4, 4, 0, 0]}>
                {providerAvg.map((entry, i) => (
                  <Cell key={i} fill={PROVIDER_COLORS[entry.provider] || '#10b981'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 3: Input vs Output split */}
        <div className="bg-card border border-border/50 rounded-xl p-5" data-testid="chart-split">
          <h3 className="text-sm font-semibold">Input vs Output Cost Split</h3>
          <p className="text-xs text-muted-foreground mt-0.5 mb-4">Top 10 cheapest — stacked (USD/month)</p>
          <ResponsiveContainer width="100%" height={290}>
            <BarChart data={inputOutputSplit} margin={{ left: 0, right: 10, top: 0, bottom: 44 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(128,128,128,0.12)" />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `$${v}`} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 10, paddingTop: 4 }} />
              <Bar dataKey="Input"  stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Output" stackId="a" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* ── Results table ── */}
      <div className="bg-card border border-border/50 rounded-xl overflow-hidden mb-10" data-testid="results-table">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-border/50">
          <div>
            <h2 className="font-semibold">All Models
              <span className="ml-2 text-xs text-muted-foreground font-normal">
                {modelsLoading ? 'Loading…' : `(${filtered.length} results)`}
              </span>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {inputTokens.toLocaleString()} input tokens · {outputTokens.toLocaleString()} output tokens · {apiCalls} calls/month
            </p>
          </div>
          <select
            value={filterProvider}
            onChange={e => setFilterProvider(e.target.value)}
            className="text-xs border border-border rounded-lg px-2.5 h-8 bg-background text-foreground"
            data-testid="filter-provider"
          >
            <option value="">All Providers</option>
            {providers.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/20">
                {[
                  { key: 'provider', label: 'Provider' },
                  { key: 'model',    label: 'Model' },
                  { key: 'context',  label: 'Context' },
                  { key: 'input',    label: 'Input / 1M tokens' },
                  { key: 'output',   label: 'Output / 1M tokens' },
                  { key: 'perCall',  label: 'Per Call' },
                  { key: 'total',    label: 'Monthly Total' },
                ].map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="px-4 py-3 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none whitespace-nowrap"
                    data-testid={`sort-${col.key}`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.label}
                      <SortIcon field={col.key} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => {
                const isCheapest = i === 0 && sortField === 'total' && sortDir === 'asc' && !filterProvider;
                return (
                  <tr
                    key={`${m.provider}-${m.model}`}
                    className={`border-b border-border/20 hover:bg-accent/30 transition-colors ${isCheapest ? 'bg-primary/5' : ''}`}
                    data-testid={`row-${m.model.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          background: `${PROVIDER_COLORS[m.provider] || '#10b981'}22`,
                          color:       PROVIDER_COLORS[m.provider] || '#10b981',
                        }}
                      >
                        {m.provider}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium whitespace-nowrap">
                      {isCheapest && <span className="mr-1.5 text-primary text-[10px] font-bold uppercase tracking-wide">Best</span>}
                      {m.model}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{m.context}</td>
                    <td className="px-4 py-3 font-mono text-xs">${(m.input * 1000).toFixed(4)}</td>
                    <td className="px-4 py-3 font-mono text-xs">${(m.output * 1000).toFixed(4)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{fmtUsd(m.perCall, 5)}</td>
                    <td className="px-4 py-3">
                      <span className={`font-bold font-mono text-sm ${
                        m.total < 0.5  ? 'text-primary' :
                        m.total < 5    ? 'text-amber-500' :
                                          'text-destructive'
                      }`}>
                        {fmtUsd(m.total, m.total < 0.01 ? 5 : 4)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        {[
          {
            q: 'What is a token?',
            a: 'A token is roughly 4 characters or ¾ of a word in English. "Hello world" is about 3 tokens. Most LLMs price per 1,000 or 1,000,000 tokens processed.',
          },
          {
            q: "What\u2019s the difference between input and output tokens?",
            a: 'Input tokens are your prompt (instructions + context). Output tokens are the model\u2019s reply. Output pricing is usually 3\u20135\u00d7 higher than input.',
          },
          {
            q: 'Why does Image Creation show text-model costs?',
            a: 'Image generation APIs (DALL-E, Midjourney) are priced per image, not per token. This preset estimates the cost of using an LLM to write/refine image prompts before sending to an image API.',
          },
          {
            q: 'How accurate are these estimates?',
            a: 'Prices reflect publicly available API pricing as of early 2026. Actual costs may vary with volume discounts, caching, fine-tuning tiers, or regional pricing.',
          },
        ].map(({ q, a }) => (
          <div key={q} className="bg-card border border-border/50 rounded-xl p-5">
            <h4 className="text-sm font-semibold mb-2">{q}</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">{a}</p>
          </div>
        ))}
      </div>

    </div>
  );
}
